/***
 *
 * Pond Size Calculator with Google Maps Integration
 *
 * Allows a user to plot points on Google Maps and determine the size of a water mass
 *
 * @param {object} (required) config - An object containing all our initialization data
 * @param {boolean} (required) config.zoom - The zoom level the map will initialize at
 * @param {boolean} (optional) config.grayScale - When true, renders the map in grayscale
 * @param {float} (required) config.defaultLatitude - The default latitude the map will initialize at if we are unable to determine user location
 * @param {float} (required) config.defaultLongitude - The default longitude the map will initialize at if we are unable to determine user location
 *
 *
 */
if (!customElements.get('pond-calculator')) {
  class PondCalculator extends HTMLElement {
    constructor() {
      super();

      this.config = this.querySelector("script[type='application/json']");
      if (!this.config) {
        console.error('Config not found.');
        return;
      }
      this.config = this.config.textContent;
      this.config = JSON.parse(this.config);

      this.zoom = this.config.zoom;
      this.grayScale = this.config.grayScale;
      this.defaultLatitude = this.config.latitude;
      this.defaultLongitude = this.config.longitude;

      this.area = 0;
      this.squareFeet = 0;
      this.acres = 0;
      this.waterVolume = 0;
      this.totalAcreFeet = 0;
      this.averageDepth = 0;

      this.mapContainer = this.querySelector('[data-map-container]');
      this.search = this.querySelector('[data-search]');
      this.depth = this.querySelector('[data-depth]');
      this.loader = this.querySelector('[data-loader]');
      this.surfaceSquareFeet = this.querySelector('[data-surface-square-feet]');
      this.surfaceSquareAcres = this.querySelector('[data-surface-acres]');
      this.estimatedWaterVolume = this.querySelector('[data-estimated-water-volume]');
      this.totalAcres = this.querySelector('[data-total-acres]');

      this.map = null;
      this.infoWindow = null;
      this.originLocation = {
        lat: this.defaultLatitude,
        lng: this.defaultLongitude
      };
      this.distanceSortedStores = [];
      this.userLocation = {};

      if (
        !this.mapContainer ||
        !this.defaultLatitude ||
        !this.defaultLongitude ||
        !this.search ||
        !this.zoom ||
        !this.surfaceSquareAcres ||
        !this.surfaceSquareFeet ||
        !this.estimatedWaterVolume ||
        !this.totalAcres
      ) {
        this.classList.add('hidden');
        console.error('Required components not found.');
        return;
      }

      this.getUserLocation();
      this.bind();
      this.init();
    }

    getUserLocation() {
      const component = this;
      if (!navigator.geolocation) {
        console.info('Geolocation not supported. Map will use the default position.');
        return;
      }

      function success(location) {
        component.userLocation.lat = location.coords.latitude;
        component.userLocation.lng = location.coords.longitude;
        component.updateMapCenter(component.userLocation);
      }

      function failure() {
        console.info('Geolocation not supported. Map will use default position.');
        return;
      }

      navigator.geolocation.getCurrentPosition(success, failure);
    }

    bind() {
      const component = this;
      this.depth.addEventListener('change', function (e) {
        if (isNaN(e.target.value) || e.target.value <= 0) {
          component.averageDepth = 0;
          e.target.value = 0;
          component.calculateMeasurements();
          return;
        }
        component.averageDepth = e.target.value;
        component.calculateMeasurements();
      });
    }

    async init() {
      const component = this;
      const styles = [{stylers: []}];
      if (component.grayScale) styles[0].stylers.push({saturation: -100});
      const {Map} = await google.maps.importLibrary('maps');
      component.map = new Map(component.mapContainer, {
        center: {
          lat: component.userLocation.lat ? component.userLocation.lat : component.originLocation.lat,
          lng: component.userLocation.lng ? component.userLocation.lng : component.originLocation.lng
        },
        zoom: component.zoom,
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        mapTypeId: 'satellite'
      });

      const controlWrapper = document.createElement('div');
      controlWrapper.classList.add('custom-controls');
      const drawButton = component.createPolyLineButton();
      const resetButton = component.createResetButton();
      controlWrapper.appendChild(drawButton);
      controlWrapper.appendChild(resetButton);
      component.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(controlWrapper);

      const {DrawingManager} = await google.maps.importLibrary('drawing');
      component.drawingManager = new DrawingManager({
        polylineOptions: {
          strokeWeight: 4,
          strokeColor: '#FF002E',
          fillOpacity: 1,
          editable: true,
          draggable: true
        },
        drawingMode: google.maps.drawing.OverlayType.POLYLINE,
        drawingControl: false
      });

      google.maps.event.addListener(component.drawingManager, 'overlaycomplete', function (e) {
        component.drawingManager.setDrawingMode(null);
        let newShape = e.overlay;
        newShape.type = e.type;
        if (component.selectedShape) {
          component.deleteSelection();
        }
        google.maps.event.addListener(newShape, 'click', function () {
          component.setSelection(newShape);
        });
        component.setSelection(newShape);
        component.calculateMeasurements();
      });

      google.maps.event.addListener(component.drawingManager, 'drawingmode_changed', component.clearSelection);
      google.maps.event.addListener(component.map, 'click', component.clearSelection);

      component.drawingManager.setMap(component.map);

      const {spherical, poly, encoding} = await google.maps.importLibrary('geometry');
      component.spherical = spherical;

      component.createAutocomplete();

      component.loader && component.loader.classList.remove('visible');
    }

    enableDrawing() {
      const component = this;
    }

    setSelection(shape) {
      const component = this;
      component.clearSelection();
      component.selectedShape = shape;
      component.selectedShape.setEditable(true);
      google.maps.event.addListener(component.selectedShape.getPath(), 'set_at', component.calculateMeasurements);
      google.maps.event.addListener(component.selectedShape.getPath(), 'insert_at', component.calculateMeasurements);
    }

    clearSelection() {
      const component = this;
      if (!component.selectedShape) return;
      component.selectedShape.setEditable(false);
      component.selectedShape = null;
    }

    deleteSelection() {
      const component = this;
      if (!component.selectedShape) return;
      component.selectedShape.setMap(null);
      component.area = 0;
      component.squareFeet = 0;
      component.acres = 0;
      component.waterVolume = 0;
      component.totalAcreFeet = 0;
      component.surfaceSquareFeet.textContent = component.squareFeet;
      component.surfaceSquareAcres.textContent = component.acres;
      component.estimatedWaterVolume.textContent = component.waterVolume;
      component.totalAcres.textContent = component.totalAcreFeet;
      component.depth.value = 0;
      component.averageDepth = 0;
    }

    async calculateMeasurements() {
      const component = this;
      if (!component.spherical || !component.selectedShape) return;
      const area = component.spherical.computeArea(component.selectedShape.getPath());
      component.area = area;
      component.squareFeet = component.area * 10.764;
      component.squareFeet = component.squareFeet.toFixed(2);
      component.acres = component.area / 4046.85642;
      component.acres = component.acres.toFixed(5);
      component.waterVolume = component.acres * 325900;
      component.waterVolume = component.waterVolume.toFixed(0);
      component.totalAcreFeet = component.acres * component.averageDepth;
      component.totalAcreFeet = component.totalAcreFeet.toFixed(4);

      component.surfaceSquareFeet.textContent = component.squareFeet
        .toString()
        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
      component.surfaceSquareAcres.textContent = component.acres
        .toString()
        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
      component.estimatedWaterVolume.textContent = component.waterVolume
        .toString()
        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
      component.totalAcres.textContent = component.totalAcreFeet
        .toString()
        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
    }

    updateMapCenter(coordinates) {
      const component = this;
      if (!component.map) return;
      const latLng = coordinates
        ? coordinates
        : new google.maps.LatLng(component.originLocation.lat, component.originLocation.lng);
      component.originLocation = latLng;
      component.map.setCenter(component.originLocation);
    }

    createResetButton() {
      const component = this;
      const button = document.createElement('button');
      button.textContent = window.accessibilityStrings.reset ? window.accessibilityStrings.reset : 'Reset';
      button.type = 'button';
      button.addEventListener('click', function () {
        component.deleteSelection();
      });
      return button;
    }

    createPolyLineButton() {
      const component = this;
      const button = document.createElement('button');
      button.innerHTML = `${window.icons.area} ${
        window.accessibilityStrings.drawArea ? window.accessibilityStrings.drawArea : 'Draw Area Around The Pond'
      }`;
      button.type = 'button';
      button.addEventListener('click', function () {
        component.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYLINE);
      });
      return button;
    }

    async createAutocomplete() {
      const component = this;

      const {Autocomplete} = await google.maps.importLibrary('places');
      component.search = new google.maps.places.Autocomplete(component.search);

      component.search.addListener('place_changed', async () => {
        component.originLocation = component.map.getCenter();
        const place = component.search.getPlace();

        if (!place.geometry) {
          console.error(`No address available for ${place.name}`);
          return;
        }

        component.updateMapCenter(place.geometry.location);
        component.map.setZoom(14);
      });
    }
  }

  customElements.define('pond-calculator', PondCalculator);
}
